import { useState } from 'react'
import { useToast } from '../../context/ToastContext'
import { apiCall } from '../../utils/api'
import { trackCreateJob } from '../../utils/analytics'

export default function NewJobModal({ onClose, onJobCreated }) {
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    contentUrl: '',
    workflow: []
  })

  const extractors = [
    { id: 'ffprobe', name: 'FFprobe (Metadata)', price: '$0.001/sec' },
    { id: 'thumbnail', name: 'Thumbnails', price: '$0.005/sec' },
    { id: 'transcription', name: 'Speech-to-Text', price: '$0.01/sec' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.contentUrl) {
      showToast('error', 'Please enter a content URL', 'Validation Error')
      return
    }

    if (formData.workflow.length === 0) {
      showToast('error', 'Please select at least one extractor', 'Validation Error')
      return
    }

    try {
      setLoading(true)
      const response = await apiCall('/v1/jobs', {
        method: 'POST',
        body: JSON.stringify({
          content_url: formData.contentUrl,
          workflow: formData.workflow.join(',')
        })
      })

      showToast('success', `Job created successfully! Job ID: ${response.job?.id?.substring(0, 8)}...`, 'Job Created')

      // Track job creation in Google Analytics
      trackCreateJob(formData.workflow.join(','))

      if (onJobCreated) {
        onJobCreated(response.job)
      }

      // Close modal after brief delay
      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (error) {
      console.error('Error creating job:', error)
      showToast('error', error.message || 'Failed to create job', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleExtractorToggle = (extractorId) => {
    setFormData(prev => ({
      ...prev,
      workflow: prev.workflow.includes(extractorId)
        ? prev.workflow.filter(id => id !== extractorId)
        : [...prev.workflow, extractorId]
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Job</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="content-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content URL
            </label>
            <input
              type="url"
              id="content-url"
              value={formData.contentUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, contentUrl: e.target.value }))}
              placeholder="https://example.com/video.mp4"
              required
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Extractors
            </label>
            <div className="space-y-2">
              {extractors.map((extractor) => {
                const isSelected = formData.workflow.includes(extractor.id)
                return (
                  <label
                    key={extractor.id}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-600 dark:bg-primary-700'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-primary-500 dark:hover:border-primary-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleExtractorToggle(extractor.id)}
                      className="w-4 h-4 text-white border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className={`text-sm font-medium ${
                        isSelected ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        {extractor.name}
                      </div>
                      <div className={`text-xs ${
                        isSelected ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {extractor.price}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Select one or more extractors to process your content
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Creating Job...
              </>
            ) : (
              <>
                <i className="fas fa-play mr-2"></i>
                Start Processing
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
